import { type ClassValue, clsx } from "clsx";
import { extendTailwindMerge } from "tailwind-merge";

// Custom @theme tokens need a class group: otherwise text roles are mistaken
// for colors, and named spacing survives conflicting padding overrides.
const twMerge = extendTailwindMerge({
  extend: {
    theme: {
      text: [
        "nf-ds-display",
        "nf-ds-section",
        "nf-ds-card-title",
        "nf-ds-title",
        "nf-ds-body",
        "nf-ds-strong",
        "nf-ds-rail",
        "nf-ds-figure",
        "nf-ds-hint",
        "nf-ds-lede",
        "nf-ds-caption",
        "nf-ds-label",
        // The size scale, not just the roles. Without these, `text-nf-ds-sm`
        // is taken for a color, collides with a real one like
        // `text-muted-foreground` in the same cn(), and the loser is dropped
        // silently — the class never reaches the DOM and the element renders
        // at the inherited size. Nothing warns; it just looks unchanged.
        "nf-ds-base",
        "nf-ds-xs",
        "nf-ds-sm",
        "nf-ds-md",
        "nf-ds-lg",
        "nf-ds-xl",
        "nf-ds-2xl",
        "nf-ds-3xl",
        "nf-ds-4xl",
        "nf-ds-5xl",
        "nf-ds-6xl",
      ],
      // Radii belong here for exactly the reason the sizes above do, and the
      // symptom is the same shape. `Skeleton` ships `rounded-nf-ds-md`, and
      // a call site asking for `rounded-nf-ds-card` got BOTH classes on the
      // element: tailwind-merge does not know `nf-ds-card` is a radius, so
      // it never drops the default and the stylesheet's order decides. The
      // email-preference skeleton came out at 8px beside a 28px card.
      //
      // `rounded-full` was losing the same argument, which is the part worth
      // noticing — a plain Tailwind class cannot override a DS one either,
      // because the conflict is only resolved once both are in the group.
      //
      // The key is `radius` rather than v2's `borderRadius`; this is
      // tailwind-merge 3.
      radius: [(value: string) => /^nf-ds-/.test(value)],
      // Shadows for the same reason. The DS `SURFACE` sets
      // `shadow-nf-ds-pane`, and a card that wants a different shadow while
      // it floats (the request offer, pinned to the foot of the page, wears
      // the sheet's) was getting both classes, with the stylesheet's order
      // deciding which the eye saw.
      shadow: [(value: string) => /^nf-ds-/.test(value)],
      spacing: [
        // The control heights belong here too. `h-nf-ds-control-lg` and
        // `h-nf-ds-control` resolve through `--spacing-nf-ds-*`, so without
        // them tailwind-merge does not know the two are the same property and
        // keeps both — the stylesheet's order then decides, and a call site
        // that asks a 52px control to be 40 silently stays 52.
        (value: string) =>
          /^nf-ds-(\d+|tight|item|block|section|card|page|control|control-sm|control-lg)$/.test(
            value,
          ),
      ],
    },
  },
});

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatElapsedTime(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes === 0) return `${seconds}s`;
  return `${minutes}m ${String(seconds).padStart(2, "0")}s`;
}

export function generateUUID(): string {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}
